import { SelectionModel } from '@angular/cdk/collections';
import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { FiscalDeviceRegistryItem, FiscalDeviceRegistryQuery } from '@skylab-shared/business';
import { EnumTranslatorBase, FiscalDeviceEventKind, FiscalDeviceEventStatus } from '@skylab-shared/dataModel';

import { NgClass } from '@angular/common';
import { EnumTranslator, L10n, L10nBase, QueryBus } from '@skylab-angular-shared/core';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import {
    ButtonComponent,
    DialogContentComponentBase,
    DialogContentComponentIns,
    DialogContentViewModel, DialogHeaderComponentIns, DialogInsService, GridInsComponent, GridTemplateDirective, Menu,
    MenuItem, MenuType, ServiceGridColumn,
    ServiceGridOptions,
    TooltipDirective
} from '@skylab-angular-shared/ui';
import { CopyToClipboardService } from '../../common/copy-to-clipboard/copy-to-clipboard.service';
import { DocumentsRouting } from '../../documents/documents-routing';

export enum EventRegisterDialogButton {
    Cancel = 1,
}

type FiscalDeviceRegistryGridColumnIdentity = 'date' | 'status' | 'name' | 'note' | 'copy';

interface FiscalDeviceEventRegisterDialogViewModel extends DialogContentViewModel {
    isFormReady: boolean;
    getStatusClassNames(status: FiscalDeviceEventStatus): string[];

    gridOptions: ServiceGridOptions<FiscalDeviceRegistryItem, FiscalDeviceRegistryGridColumnIdentity, unknown>;
    gridColumns$: BehaviorSubject<ServiceGridColumn<FiscalDeviceRegistryGridColumnIdentity, unknown, FiscalDeviceRegistryItem, unknown, any>[]>;
    gridDataSource$: BehaviorSubject<FiscalDeviceRegistryItem[]>;
}

@Component({
    selector: 'skl-fiscal-device-registry-dialog',
    templateUrl: './fiscal-device-registry-dialog.component.html',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [DialogContentComponentIns, DialogHeaderComponentIns, GridInsComponent, GridTemplateDirective, TooltipDirective, NgClass, ButtonComponent, TranslatePipe]
})
export class FiscalDeviceRegistryDialogComponent extends DialogContentComponentBase<
    FiscalDeviceEventRegisterDialogViewModel
> {
    public constructor(
        private readonly queryBus: QueryBus,
        @Inject(L10n) private readonly l10n: L10nBase,
        private readonly copyToClipboardService: CopyToClipboardService,
        @Inject(EnumTranslator) private readonly enumTranslator: EnumTranslatorBase,
        dialogService: DialogInsService
    ) {
        super(dialogService);
    }

    protected override getInitialState(): FiscalDeviceEventRegisterDialogViewModel {
        return {
            isFormReady: false,
            gridOptions: {
                selection: new SelectionModel(),
                contextMenu: this.gridRowContextMenu,
                stickyHeader: false,
            },
            gridDataSource$: new BehaviorSubject<FiscalDeviceRegistryItem[]>([]),
            gridColumns$: new BehaviorSubject(this.gridColumns),
            getStatusClassNames: this.getStatusClassNames.bind(this),
        };
    }

    protected override async prepareDataAsync() {
        await this.prepareViewModelAsync();
    }

    protected override isReady(): void {
        this.viewModel.isFormReady = true;
    }

    private async prepareViewModelAsync(): Promise<void> {
        const query = new FiscalDeviceRegistryQuery();
        const collection = await this.queryBus.queryAsync(query, this.data);
        this.viewModel.gridDataSource$.next(collection);
    }

    private get gridColumns(): ServiceGridColumn<FiscalDeviceRegistryGridColumnIdentity, unknown, FiscalDeviceRegistryItem, unknown, any>[] {
        return [
            {
                identityName: 'date',
                header: this.l10n._T('scenes.configuration.shared.fiscalization.event-register-dialog.grid.columns.date'),
                columnDef: 'date',
                cell: entity => entity.date,
                width: '190px',
                isMovable: false,
                isResizable: false
            },
            {
                identityName: 'status',
                columnDef: 'status',
                header: this.l10n._T('scenes.configuration.shared.fiscalization.event-register-dialog.grid.columns.status'),
                styleClass: 'column--align-center',
                template: 'fiscalDeviceRegistryDialogGridCellStatus',
                width: '75px',
                isMovable: false,
                isResizable: false
            },
            {
                identityName: 'name',
                header: this.l10n._T('scenes.configuration.shared.fiscalization.event-register-dialog.grid.columns.document'),
                cell: entity => entity.associatedDocumentNumber ?? '',
                template: 'sklHrefLinkTemplate',
                columnDef: 'name',
                getRouteObject: (entity: FiscalDeviceRegistryItem) => {
                    if (entity.kind === FiscalDeviceEventKind.DocumentFiscalization) {
                        return {url: DocumentsRouting.getEditState(entity.associatedDocumentKind).url, params: { id: entity.associatedDocumentId }};
                    }
                    return null;
                },
                target: '_blank',
                width: '150px',
                isMovable: false,
                isResizable: false
            },
            {
                identityName: 'note',
                header: this.l10n._T('scenes.configuration.shared.fiscalization.event-register-dialog.grid.columns.note'),
                width: '150px',
                fillingColumn: true,
                cell: entity => entity.note,
                columnDef: 'note',
                isMovable: false,
                isResizable: false
            },
            {
                identityName: 'copy',
                columnDef: 'copy',
                template: 'buttonTemplate',
                action: (_, entity) => this.onCopyToClipboard(entity),
                icon: 'list-copy',
                styleClass: ['cell--button', 'cell--show-on-hover', 'cell--sticky', 'cell--no-space'],
                isMovable: false,
                isResizable: false,
                cellTooltip: _ => this.l10n._T('scenes.configuration.shared.fiscalization.event-register-dialog.menu.copy')
            }
        ];
    }

    private get gridRowContextMenu(): Menu<FiscalDeviceRegistryItem> {
        return new Menu<FiscalDeviceRegistryItem>(
            [
                new MenuItem<FiscalDeviceRegistryItem>({
                    label: this.l10n._T('scenes.configuration.shared.fiscalization.event-register-dialog.menu.copy'),
                    icon: 'icon-list-copy',
                    click: (entity?: FiscalDeviceRegistryItem) => {
                        if (entity) {
                            this.onCopyToClipboard(entity);
                        }
                    },
                }),
            ],
            {
                type: MenuType.Context,
                hideOnScroll: true,
            },
        );
    }

    public getStatusClassNames(status: FiscalDeviceEventStatus): string[] {
        switch (status) {
            case FiscalDeviceEventStatus.OK:
                return ['text--success', 'icon-check-circle'];
            case FiscalDeviceEventStatus.Warning:
                return ['text--warning', 'icon-exclamation-circle'];
            case FiscalDeviceEventStatus.Error:
                return ['text--error', 'icon-exclamation-circle'];
            case FiscalDeviceEventStatus.Info:
                return ['text--info', 'icon-info'];
        }
    }

    public getStatusText(status: FiscalDeviceEventStatus): string {
        return this.enumTranslator.getLabeledEnumValue(FiscalDeviceEventStatus, status).label;
    }

    public onCopyToClipboard(entity: FiscalDeviceRegistryItem): void {
        const infoObj = {
            deviceId: this.data,
            eventId: entity.id,
            date: entity.date,
            status: entity.status,
            statusText: this.getStatusText(entity.status),
            note: entity.note,
            linkedDocument: {
                id: entity.associatedDocumentId,
                name: entity.associatedDocumentNumber,
            },
        };

        this.copyToClipboardService.copy(JSON.stringify(infoObj));
    }
}
